import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent, {
  timelineContentClasses,
} from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import { DetectionStatus } from "../../../../utils/constants";
import {
  getPrettyDate,
  getPrettyDateTime,
  getReadableTime,
  useLocalTime,
} from "../../../../utils/timestamp";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import DetectionDetails from "./DetectionDetails";

const DetectionTimeline = ({ detections, handleMoveToControl = null }) => {
  const colors = useColors();
  const showLocalTime = useLocalTime();

  const handleTextClick = (event) => {
    // Prevent accordion from toggling when selecting text
    event.stopPropagation();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Timeline
        position="right"
        sx={{
          display: "flex",
          alignItems: "stretch",
          p: 0,
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.2,
            paddingLeft: 0,
          },
          [`& .${timelineContentClasses.root}`]: {
            flex: 1.2,
          },
        }}
      >
        {detections.map((detection, index) => {
          const resolvedTooltipText =
            detection.status === DetectionStatus.RESOLVED &&
            detection.resolution_details &&
            "Resolved by " +
              detection.resolution_details.resolver_full_name +
              " on " +
              getPrettyDateTime(
                detection.resolution_details.resolution_date.timestamp_utc,
                showLocalTime
              );
          return (
            <TimelineItem key={index}>
              <TimelineOppositeContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Typography variant="overline">
                    {getReadableTime(
                      detection.date.timestamp_utc,
                      showLocalTime
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text_secondary,
                    }}
                  >
                    {getPrettyDate(detection.date.timestamp_utc, showLocalTime)}
                  </Typography>
                </Box>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    padding: "1px",
                  }}
                />
                {index < detections.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent
                sx={{
                  pr: "0",
                }}
              >
                <Accordion
                  sx={{
                    border: "1px solid " + colors.divider2,
                    boxShadow: "none",
                  }}
                  style={{ borderRadius: "8px" }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          width: "16px",
                        }}
                      />
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "row-reverse",
                      gap: "8px",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <IntegrationImage
                          name={detection.tool}
                          width={"24px"}
                          newLogo={true}
                        />
                        <Typography
                          variant="subtitle2"
                          onClick={handleTextClick}
                          sx={{
                            cursor: "text",
                            userSelect: "text",
                          }}
                        >
                          <ReactMarkdown
                            components={{
                              code: ({ node, ...props }) => (
                                <code
                                  style={{
                                    backgroundColor: colors.code_background,
                                    padding: "2px 4px",
                                    borderRadius: "4px",
                                    fontFamily: "monospace",
                                    fontSize: "0.9em",
                                  }}
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {detection.title}
                          </ReactMarkdown>
                        </Typography>
                      </Box>
                      <ChipColor
                        value={detection?.severity}
                        type={"controlPriority"}
                        size="small"
                        labelWidth={true}
                        style={{
                          borderRadius: "10px",
                        }}
                      />
                    </Box>
                    {detection.status === DetectionStatus.RESOLVED && (
                      <CustomToolTip title={resolvedTooltipText}>
                        <DoneRoundedIcon
                          sx={{
                            width: "16px",
                            color: colors.primary_main,
                          }}
                        />
                      </CustomToolTip>
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    <DetectionDetails
                      detection={detection}
                      handleMoveToControl={handleMoveToControl}
                      toolTipText={resolvedTooltipText}
                    />
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default DetectionTimeline;
